import FormValidations from './FormValidations';

export default class FormField extends FormValidations {
  /**
   * constructor description
   * @param {[String]} alias - An alias for this field
   * @param {[Number]} greaterThan - A number that field value need be bigger
   * @param {[Boolean]} cpf - True if need validate field using CPF rule
   * @param {[Boolean]} date - True if need validate field using Date rule
   * @param {[Boolean]} email - True for validates field as email
   * @param {[Array]} errors - Errors found after field validate
   * @param {[Boolean]} fullName - True for validates field as full name
   * @param {[Boolean]} aux - The function getRawValue will ignore this field
   * @param {[Number]} lowerThan - A number that field value need be lower
   * @param {[String]} matchPassword - A field name that this field value should be equal
   * @param {[Number]} maxLength - A number that field length need be lower
   * @param {[Number]} minLength - A number that field length need be bigger
   * @param {[Regex]} regex - A regex for validates field
   * @param {[Boolean]} required - True for validates field as required
   * @param {[Object]} ignoreIfNot - An object that contains { fieldName: value|Array of values } that will make form
   * not ignore this field when fieldname matchs the value
   * @param {[String]} getValueFrom - Get value from an specific field in the form
   * @param {[Any]} value - Any value for this field
   * @param {[Boolean]} zipCode - True for validates field as zip code
   * @param {[Boolean]} valid - Turns the field valid
   * @param {[Object]} requiredIfMatch - make the field required depending of another fields condition matchs
   * { fieldName: { value: value, condition: '><=!' }}
   * @param {[Object]} differentFrom - A object that contains a { alias: alias|String, value: value }, will validates
   * that the object value has to be differentFrom the hash value
   */
  constructor(params = {}) {
    super({
      value: params.value,
      maxLength: params.maxLength,
      minLength: params.minLength,
      greaterThan: params.greaterThan,
      lowerThan: params.lowerThan,
      regex: params.regex,
      cpf: params.cpf,
      date: params.date,
      email: params.email,
      fullName: params.fullName,
      zipCode: params.zipCode,
      differentFrom: params.differentFrom,
      maxAge: params.maxAge,
      minAge: params.minAge,
      valid: params.valid,
    });

    this.onlyLettersAndSpaces = params.onlyLettersAndSpaces;
    this.alias = params.alias;
    this.errors = params.errors || [];
    this.getValueFrom = params.getValueFrom;
    this.ignoreOnGetRawValue = params.ignoreOnGetRawValue;
    this.matchPassword = params.matchPassword;
    this.required = params.required;
    this.ignoreIfNot = params.ignoreIfNot;
    this.requiredIfMatch = params.requiredIfMatch;
    this.valid = params.valid;
    this.onAddValueReplicateTo = params.onAddValueReplicateTo;
    this.error = this.errors[0] || '';
  }

  get hasError() {
    return Boolean(this.errors && this.errors.length);
  }

  isFieldNotRequiredAndEmpty() {
    return !this.required && this.empty();
  }

  validateField(hideErrors) {
    const errors = [];

    if (this.required && this.empty()) {
      errors.push(this.requiredError());
    } else if (!this.isFieldNotRequiredAndEmpty()) {
      if (this.differentFrom && !this.validateDifferentFrom()) {
        errors.push(this.differentFromError(this.differentFrom.alias));
      }

      if (this.cpf && !this.validateCpf()) {
        errors.push(this.cpfError());
      }

      if ((this.maxLength || this.maxLength === 0) && !this.validateMaxLength()) {
        errors.push(this.maxLengthError(this.maxLength));
      }

      if ((this.minLength || this.minLength === 0) && !this.validateMinLength()) {
        errors.push(this.minLengthError(this.minLength));
      }

      if ((this.greaterThan || this.greaterThan === 0) && !this.validateGreaterThan()) {
        errors.push(this.greaterThanError(this.greaterThan));
      }

      if ((this.lowerThan || this.lowerThan === 0) && !this.validateLowerThan()) {
        errors.push(this.lowerThanError(this.lowerThan));
      }

      if (this.email && !this.validateEmail()) {
        errors.push(this.emailError());
      }

      if (this.date && !this.validateDate()) {
        errors.push(this.dateError());
      }

      if (this.maxAge && !this.validateMaxAge()) {
        errors.push(this.maxAgeError(this.maxAge));
      }

      if (this.minAge && !this.validateMinAge()) {
        errors.push(this.minAgeError(this.minAge));
      }

      if (this.zipCode && !this.validateZipCode()) {
        errors.push(this.zipCodeError());
      }

      if (this.fullName && !this.validateFullName()) {
        errors.push(this.fullNameError());
      }

      if (this.regex && !this.validateRegex()) {
        errors.push(this.invalidValueError());
      }

      if (this.onlyLettersAndSpaces && !this.validateOnlyLettersAndSpaces()) {
        errors.push(this.onlyLettersAndSpacesError());
      }
    }

    this.valid = errors.length === 0;

    if (!hideErrors) {
      this.errors = errors;
      this.error = this.errors[0] || '';
    }

    return this.valid;
  }

  empty() {
    return this.value === null || this.value === '' || this.value === undefined;
  }
}
